/**
 * Payroll export — hours per worker and per site, as Excel and PDF.
 *
 * Auto-closed and disputed shifts are excluded from the totals and the export unless
 * explicitly included. An auto-closed shift is a guess the system made because someone
 * forgot to clock out; paying it unreviewed is the mistake the whole flag system exists
 * to prevent.
 */

import { Fragment, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { MobileList } from '@/components/MobileList';
import { Badge, Card, ErrorNotice, Field, PageHeader, Spinner, Stat } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api, ApiError } from '@/lib/api';
import { dateShort, decimalHours, duration, isoDate, time } from '@/lib/format';

function startOfMonth(): string {
  const d = new Date();
  d.setDate(1);
  return isoDate(d);
}

export function PayrollPage() {
  const { t, plural } = useI18n();
  const [from, setFrom] = useState(startOfMonth);
  const [to, setTo] = useState(() => isoDate(new Date()));
  const [group, setGroup] = useState<'worker' | 'site'>('worker');
  const [includeUnreviewed, setIncludeUnreviewed] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const report = useQuery({
    queryKey: ['payroll', from, to, group, includeUnreviewed],
    queryFn: () => api.payroll({ from, to, groupBy: group, includeUnreviewed }),
  });

  const download = useMutation({
    mutationFn: (format: 'xlsx' | 'pdf') =>
      api.downloadPayroll({ from, to, groupBy: group, format, includeUnreviewed }),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    },
  });

  const groups = report.data?.groups ?? [];
  const totals = report.data?.totals;

  const setMonth = (offset: number) => {
    const d = new Date();
    d.setMonth(d.getMonth() + offset, 1);
    const first = isoDate(d);
    const last = isoDate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
    setFrom(first);
    setTo(last);
  };

  return (
    <>
      <PageHeader
        title={t('payroll.title')}
        subtitle={t('payroll.subtitle')}
        actions={
          <>
            <button
              type="button"
              className="btn-secondary"
              disabled={groups.length === 0 || download.isPending}
              onClick={() => download.mutate('pdf')}
            >
              {t('payroll.downloadPdf')}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={groups.length === 0 || download.isPending}
              onClick={() => download.mutate('xlsx')}
            >
              {download.isPending ? t('payroll.preparing') : t('payroll.downloadExcel')}
            </button>
          </>
        }
      />

      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-4">
          <Field label={t('common.from')}>
            <input type="date" className="input w-40" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label={t('common.to')}>
            <input type="date" className="input w-40" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>

          <div className="flex gap-2">
            <button type="button" className="btn-secondary" onClick={() => setMonth(0)}>
              {t('common.thisMonth')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setMonth(-1)}>
              {t('common.lastMonth')}
            </button>
          </div>

          <Field label={t('payroll.groupBy')}>
            <div className="flex">
              {(['worker', 'site'] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGroup(g)}
                  className={`h-9 px-4 text-sm font-semibold border transition-colors ${
                    g === 'worker' ? 'rounded-s' : 'rounded-e -ms-px'
                  } ${
                    group === g
                      ? 'bg-primary text-[var(--color-on-primary)] border-primary'
                      : 'bg-surface text-muted border-border hover:bg-surface-alt'
                  }`}
                >
                  {t(g === 'worker' ? 'payroll.byWorker' : 'payroll.bySite')}
                </button>
              ))}
            </div>
          </Field>

          <label className="flex items-center gap-2 text-sm h-9">
            <input
              type="checkbox"
              checked={includeUnreviewed}
              onChange={(e) => setIncludeUnreviewed(e.target.checked)}
            />
            {t('payroll.includeUnreviewed')}
          </label>
        </div>
      </Card>

      {download.error ? (
        <Card className="mb-4 border-[var(--color-danger)]">
          <p className="text-sm text-danger">
            {download.error instanceof ApiError
              ? download.error.message
              : t('payroll.exportFailed')}
          </p>
        </Card>
      ) : null}

      {totals && totals.excluded > 0 && !includeUnreviewed ? (
        <Card className="mb-4 bg-warning-soft border-[var(--color-warning)]">
          <p className="text-sm">
            {plural(totals.excluded, 'payroll.excludedOne', 'payroll.excludedMany', {
              count: totals.excluded,
            })}
          </p>
        </Card>
      ) : null}

      {report.isLoading ? (
        <Spinner />
      ) : report.error ? (
        <ErrorNotice error={report.error} />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Stat
              label={t('payroll.totalHours')}
              value={duration(totals?.minutes ?? 0)}
              tone="primary"
              hint={`${decimalHours(totals?.minutes ?? 0)} ore decimali`}
            />
            <Stat label={t('payroll.countedShifts')} value={totals?.shifts ?? 0} />
            <Stat
              label={t('payroll.excluded')}
              value={totals?.excluded ?? 0}
              tone={(totals?.excluded ?? 0) > 0 ? 'warning' : 'neutral'}
            />
          </div>

          {groups.length === 0 ? (
            <Card>
              <p className="text-sm text-muted">{t('payroll.noShifts')}</p>
            </Card>
          ) : (
            <Card padded={false}>
              <div className="lg:hidden p-2">
                  {/* The per-shift breakdown stays behind the sheet: on a phone the
                      question is "how many hours for whom", and the shift list is what
                      you open afterwards on a bigger screen. */}
                  <MobileList
                    rows={groups}
                    keyOf={(g) => g.label}
                    title={(g) => g.label}
                    subtitle={(g) =>
                      plural(g.shifts, 'shifts.countOne', 'shifts.countMany', { count: g.shifts })
                    }
                    trailing={(g) => duration(g.minutes)}
                    badges={(g) =>
                      g.excluded > 0 ? (
                        <Badge tone="warning">
                          {t('payroll.toVerify')}: {g.excluded}
                        </Badge>
                      ) : null
                    }
                    fields={[
                      { label: t('common.hours'), value: (g) => duration(g.minutes) },
                      { label: t('payroll.decimalHours'), value: (g) => decimalHours(g.minutes) },
                      { label: t('nav.shifts'), value: (g) => String(g.shifts) },
                    ]}
                  />
                </div>

              <div className="table-scroll max-lg:hidden">
                <table className="table-wide">
                  <thead>
                    <tr>
                      <th className="th">{t(group === 'worker' ? 'payroll.byWorker' : 'payroll.bySite')}</th>
                      <th className="th">{t('nav.shifts')}</th>
                      <th className="th">{t('common.hours')}</th>
                      <th className="th">{t('payroll.decimalHours')}</th>
                      <th className="th">{t('payroll.toVerify')}</th>
                      <th className="th" />
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g) => (
                      <Fragment key={g.label}>
                        <tr className="hover:bg-surface-alt">
                          <td className="td font-semibold">{g.label}</td>
                          <td className="td tabular">{g.shifts}</td>
                          <td className="td tabular font-semibold">{duration(g.minutes)}</td>
                          <td className="td tabular text-muted">{decimalHours(g.minutes)}</td>
                          <td className="td">
                            {g.excluded > 0 ? (
                              <Badge tone="warning">{g.excluded}</Badge>
                            ) : (
                              <span className="text-subtle">—</span>
                            )}
                          </td>
                          <td className="td text-end">
                            <button
                              type="button"
                              className="td-action text-primary hover:underline"
                              onClick={() => setExpanded(expanded === g.label ? null : g.label)}
                            >
                              {t(expanded === g.label ? 'payroll.hide' : 'payroll.detail')}
                            </button>
                          </td>
                        </tr>

                        {expanded === g.label
                          ? g.rows.map((r, i) => (
                              <tr key={`${g.label}-${i}`} className="bg-surface-alt text-sm">
                                <td className="td ps-8 text-muted">
                                  {group === 'worker' ? (r.siteName ?? '—') : r.userName}
                                </td>
                                <td className="td tabular text-muted whitespace-nowrap" colSpan={2}>
                                  {dateShort(r.date)} · {time(r.clockIn)}
                                  {r.clockOut ? `–${time(r.clockOut)}` : ''}
                                </td>
                                <td className="td tabular">{duration(r.durationMinutes)}</td>
                                <td className="td" colSpan={2}>
                                  <div className="flex flex-wrap gap-1 justify-end">
                                    {r.status !== 'closed' ? (
                                      <Badge tone="warning">
                                        {t(r.status === 'auto_closed' ? 'payroll.autoClosed' : 'payroll.disputed')}
                                      </Badge>
                                    ) : null}
                                    {r.withinGeofence === false ? (
                                      <Badge tone="warning">{t('common.outsideArea')}</Badge>
                                    ) : null}
                                    {r.withinGeofence === null ? (
                                      <Badge tone="neutral">GPS n/d</Badge>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            ))
                          : null}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </>
  );
}
